import React from 'react';
import {Heart, Mail, MessageCircle, Repeat2, UserPlus, type LucideIcon} from 'lucide-react';
import {useSelector} from 'react-redux';
import {notificationApi, NotificationPreferences} from '../../../../services/notificationApi';
import {RootState} from '../../../../store';
import styles from '../Settings.module.css';

type NotificationPreferenceKey = keyof Omit<NotificationPreferences, 'updatedAt'>;

const notificationPreferenceOptions: Array<{
  key: NotificationPreferenceKey;
  label: string;
  icon: LucideIcon;
}> = [
  {key: 'messages', label: 'New message notifications', icon: Mail},
  {key: 'reelShares', label: 'Reel shares from athletes', icon: Mail},
  {key: 'follows', label: 'New follower notifications', icon: UserPlus},
  {key: 'comments', label: 'Comment notifications', icon: MessageCircle},
  {key: 'likes', label: 'Like notifications', icon: Heart},
  {key: 'reposts', label: 'Repost notifications', icon: Repeat2},
];

const notificationHeaderIconStyle = {
  '--settings-notification-icon-url': `url("${process.env.PUBLIC_URL}/icons/notification-line.svg")`,
} as React.CSSProperties;

const SettingsAccountSection: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.user.currentUser);
  const [preferences, setPreferences] = React.useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [savingKey, setSavingKey] = React.useState<NotificationPreferenceKey | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    const loadPreferences = async () => {
      if (!token) {
        if (isMounted) {
          setPreferences(null);
          setLoading(false);
          setError('Sign in to manage notification settings.');
        }
        return;
      }

      setLoading(true);
      try {
        const nextPreferences = await notificationApi.getPreferences(token);
        if (isMounted) {
          setPreferences(nextPreferences);
          setError(null);
        }
      } catch (loadError) {
        if (isMounted) setError(loadError instanceof Error ? loadError.message : 'Failed to load notification settings.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    void loadPreferences();
    return () => { isMounted = false; };
  }, [token]);

  const handleToggle = async (key: NotificationPreferenceKey, nextValue: boolean) => {
    if (!token || !preferences || savingKey) return;
    const previousPreferences = preferences;
    setPreferences({...preferences, [key]: nextValue, updatedAt: new Date().toISOString()});
    setSavingKey(key);
    setError(null);
    try {
      setPreferences(await notificationApi.updatePreferences(token, {[key]: nextValue}));
    } catch (saveError) {
      setPreferences(previousPreferences);
      setError(saveError instanceof Error ? saveError.message : 'Failed to save notification settings.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className={`${styles.settingsCard} ${styles.notificationCard}`}>
      <div className={styles.sectionHeadingRow}>
        <span className={styles.sectionIcon}>
          <span className={styles.notificationHeaderIconAsset} style={notificationHeaderIconStyle} aria-hidden="true" />
        </span>
        <div>
          <h3 className={styles.settingsCardTitle}>Notification Settings</h3>
          <p className={styles.settingsCardSubtitle}>Choose what you want to be notified about.</p>
        </div>
      </div>

      {error ? <p className={styles.inlineErrorText}>{error}</p> : null}

      <div className={styles.notificationHeader} aria-hidden="true">
        <span />
        <span>In-App</span>
      </div>

      <div className={styles.notificationList}>
        {notificationPreferenceOptions.filter((option) => (
          option.key !== 'reelShares'
          || user?.enterprise?.role === 'coach'
          || user?.enterprise?.role === 'recruiter'
        )).map((option) => {
          const Icon = option.icon;
          const checked = Boolean(preferences?.[option.key]);
          return (
            <div key={option.key} className={styles.notificationRow}>
              <div className={styles.notificationLabel}><Icon size={18} aria-hidden="true" /><span>{option.label}</span></div>
              <label className={styles.switchLabel} aria-label={`${option.label}, in-app`}>
                <input
                  type="checkbox"
                  className={styles.switchInput}
                  checked={checked}
                  disabled={loading || savingKey === option.key}
                  onChange={(event) => void handleToggle(option.key, event.target.checked)}
                />
                <span className={styles.switchTrack} />
              </label>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default React.memo(SettingsAccountSection);
